import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { Layout } from '../components/Layout';

interface CronjobLog {
  id: number;
  total_actions: number;
  status: string;
  error_message?: string | null;
  details?: {
    job_type: string;
    executed_at?: string;
    success_count: number;
    failed_count: number;
    success_reservations?: Array<{
      reservation_id?: number;
      reservation_number: string;
      vehicle_name?: string;
      start_date?: string;
      end_date?: string;
      from_status?: string;
      to_status?: string;
      status_changed?: string;
      wooky_error?: string;
      review_request_sent?: boolean;
      review_request_error?: string;
    }>;
    success_notifications?: Array<{
      reservation_id?: number;
      reservation_number: string;
      vehicle_name?: string;
      return_date?: string;
      return_location?: string;
      notification_type?: string;
      reminder_type?: string;
      sent_at?: string;
      status?: string;
    }>;
    failed_reservations?: Array<{
      reservation_id?: number;
      reservation_number: string;
      vehicle_name?: string;
      error: string;
      status?: string;
    }>;
    failed_notifications?: Array<{
      reservation_id?: number;
      reservation_number: string;
      vehicle_name?: string;
      reminder_type?: string;
      error: string;
    }>;
  };
  created_at: string;
}

export const CronjobLogsPage: React.FC = () => {
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null);

  const { data: logs, isLoading, error } = useQuery({
    queryKey: ['cronjob-logs'],
    queryFn: async () => {
      const response = await apiClient.get<CronjobLog[]>('/admin/cronjob-logs');
      return response.data;
    },
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'partial_success':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'success':
        return '성공';
      case 'partial_success':
        return '부분 성공';
      case 'failed':
        return '실패';
      default:
        return status;
    }
  };

  const getJobTypeLabel = (jobType?: string) => {
    switch (jobType) {
      case 'auto_pickup':
        return '자동 출고';
      case 'auto_return':
        return '자동 반납';
      case 'return_reminder':
        return '반납 알림';
      case 'review_request':
        return '리뷰 요청';
      default:
        return jobType || '-';
    }
  };

  const toggleExpand = (logId: number) => {
    setExpandedLogId(expandedLogId === logId ? null : logId);
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">스케줄링 실행 로그</h1>
          <p className="mt-1 text-sm text-gray-600">
            자동으로 실행되는 예약 상태 업데이트 및 알림 발송 작업 기록
          </p>
        </div>

        {isLoading && (
          <div className="text-center py-8">
            <div className="text-gray-500">로딩 중...</div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
            오류가 발생했습니다: {(error as any).message}
          </div>
        )}

        {logs && (
          <div className="bg-white shadow-sm rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    작업 유형
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    작업 개수
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    실행 시간
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상세
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                      실행 기록이 없습니다.
                    </td>
                  </tr>
                ) : (
                  <>
                    {logs.map((log) => {
                      const isExpanded = expandedLogId === log.id;

                      return (
                        <React.Fragment key={log.id}>
                          {/* 메인 행 */}
                          <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleExpand(log.id)}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {log.id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {getJobTypeLabel(log.details?.job_type)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {log.total_actions}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(
                                  log.status
                                )}`}
                              >
                                {getStatusLabel(log.status)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(log.created_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <button
                                className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpand(log.id);
                                }}
                              >
                                {isExpanded ? (
                                  <>
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                    접기
                                  </>
                                ) : (
                                  <>
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                    펼치기
                                  </>
                                )}
                              </button>
                            </td>
                          </tr>

                          {/* 확장 행 */}
                          {isExpanded && (
                            <tr>
                              <td colSpan={6} className="px-6 py-4 bg-gray-50">
                                <div className="space-y-4">
                                  {/* 요약 정보 */}
                                  <div className="text-sm text-gray-700 mb-4">
                                    <span className="font-medium">{getJobTypeLabel(log.details?.job_type)}</span>
                                    <span className="mx-2">|</span>
                                    <span>총 {log.total_actions}건</span>
                                    <span className="mx-2">|</span>
                                    <span className="text-green-600">성공 {log.details?.success_count || 0}건</span>
                                    <span className="mx-2">|</span>
                                    <span className="text-red-600">실패 {log.details?.failed_count || 0}건</span>
                                  </div>

                                  {/* 에러 메시지 */}
                                  {log.error_message && (
                                    <div className="bg-red-50 p-3 rounded border border-red-200">
                                      <div className="text-xs text-red-600 font-medium mb-1">오류 메시지</div>
                                      <div className="text-sm text-red-800">{log.error_message}</div>
                                    </div>
                                  )}

                                  {/* 성공한 예약 처리 */}
                                  {log.details?.success_reservations && log.details.success_reservations.length > 0 && (
                                    <div className="bg-green-50 p-3 rounded border border-green-300">
                                      <h4 className="text-xs font-semibold text-green-700 mb-2">✓ 성공 ({log.details.success_reservations.length}건)</h4>
                                      <div className="space-y-1">
                                        {log.details.success_reservations.map((item, idx) => (
                                          <div key={idx} className="text-xs text-gray-700">
                                            <span className="font-medium">{item.reservation_number}</span>
                                            {item.vehicle_name && <span> | {item.vehicle_name}</span>}
                                            {(item.from_status && item.to_status) && (
                                              <span className="text-blue-700"> | {item.from_status} → {item.to_status}</span>
                                            )}
                                            {item.start_date && <span> | {item.start_date} ~ {item.end_date}</span>}
                                            {item.wooky_error && <span className="text-orange-600"> ⚠️ {item.wooky_error}</span>}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* 성공한 알림 발송 */}
                                  {log.details?.success_notifications && log.details.success_notifications.length > 0 && (
                                    <div className="bg-green-50 p-3 rounded border border-green-300">
                                      <h4 className="text-xs font-semibold text-green-700 mb-2">✓ 성공 ({log.details.success_notifications.length}건)</h4>
                                      <div className="space-y-1">
                                        {log.details.success_notifications.map((item, idx) => (
                                          <div key={idx} className="text-xs text-gray-700">
                                            <span className="font-medium">{item.reservation_number}</span>
                                            {item.vehicle_name && <span> | {item.vehicle_name}</span>}
                                            {item.reminder_type && <span> | {item.reminder_type === 'first' ? '1차' : '2차'} 리마인더</span>}
                                            {item.return_date && <span> | {item.return_date}</span>}
                                            {item.return_location && <span> | {item.return_location}</span>}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* 실패한 예약 처리 */}
                                  {log.details?.failed_reservations && log.details.failed_reservations.length > 0 && (
                                    <div className="bg-red-50 p-3 rounded border border-red-300">
                                      <h4 className="text-xs font-semibold text-red-700 mb-2">✗ 실패 ({log.details.failed_reservations.length}건)</h4>
                                      <div className="space-y-1">
                                        {log.details.failed_reservations.map((item, idx) => (
                                          <div key={idx} className="text-xs text-gray-700">
                                            <div>
                                              <span className="font-medium">{item.reservation_number}</span>
                                              {item.vehicle_name && <span> | {item.vehicle_name}</span>}
                                            </div>
                                            <div className="text-red-700 ml-4">→ {item.error}</div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* 실패한 알림 발송 */}
                                  {log.details?.failed_notifications && log.details.failed_notifications.length > 0 && (
                                    <div className="bg-red-50 p-3 rounded border border-red-300">
                                      <h4 className="text-xs font-semibold text-red-700 mb-2">✗ 실패 ({log.details.failed_notifications.length}건)</h4>
                                      <div className="space-y-1">
                                        {log.details.failed_notifications.map((item, idx) => (
                                          <div key={idx} className="text-xs text-gray-700">
                                            <div>
                                              <span className="font-medium">{item.reservation_number}</span>
                                              {item.vehicle_name && <span> | {item.vehicle_name}</span>}
                                              {item.reminder_type && <span> | {item.reminder_type === 'first' ? '1차' : '2차'} 리마인더</span>}
                                            </div>
                                            <div className="text-red-700 ml-4">→ {item.error}</div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};
